import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Table,
  Thead,
  Tbody,
  InputGroup,
  InputAddon,
  Link,
  useToast
} from '@chakra-ui/react'
import { QRCodeCanvas } from 'qrcode.react'
import { useEffect, useState } from 'react'

import UrlShortenerService, {
  IGetUrlShortenerClicks,
  IGetUrlShortenerResponse
} from '../../../services/UrlShortener/UrlShortenerService'

export function UrlShortenerViewModal({ onClose, id }: { onClose: () => void; id: number }) {
  const toast = useToast()
  const [urlData, setUrlData] = useState<null | IGetUrlShortenerResponse>(null)
  const [filteredClicks, setFilteredClicks] = useState<IGetUrlShortenerClicks[]>([])
  const [fullUrl, setFullUrl] = useState<string>('')
  const [filters, setFilters] = useState({
    created_at: {
      from: '',
      to: ''
    }
  })

  useEffect(() => {
    loadUrlData()
  }, [id])

  useEffect(() => {
    setFilteredClicks(
      urlData?.clicks
        .filter((click) => {
          const clickedAt = new Date(click.clicked_at)
          const fromDate = filters.created_at.from ? new Date(filters.created_at.from) : null
          const toDate = filters.created_at.to ? new Date(filters.created_at.to) : null
          return (!fromDate || clickedAt >= fromDate) && (!toDate || clickedAt <= toDate)
        })
        .sort((a, b) => new Date(b.clicked_at).getTime() - new Date(a.clicked_at).getTime()) || []
    )
  }, [urlData, filters])

  const loadUrlData = async () => {
    const data = await UrlShortenerService.get(id)

    setFullUrl(`https://abra.com.br/short/${data.short_path}`)
    setUrlData(data)
  }

  const handleDownload = () => {
    const canvas = document.getElementById('abra-qrcode-canvas') as HTMLCanvasElement | null
    if (!canvas) return
    const pngUrl = canvas.toDataURL('image/png')
    const downloadLink = document.createElement('a')
    downloadLink.href = pngUrl
    downloadLink.download = `${urlData?.title || 'qrcode'}.png`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const save = async () => {
    if (!urlData) return

    try {
      await UrlShortenerService.update(urlData.id, {
        title: urlData.title,
        original_url: urlData.original_url,
        short_path: urlData.short_path
      })

      setUrlData({ ...urlData, title: urlData.title })
      toast({
        title: 'Título atualizado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (error) {
      console.error('Erro ao salvar o título:', error)
    }
  }

  return urlData ? (
    <ChakraModal isOpen={true} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Detalhes</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <div className="row">
            <div className="col-12 col-sm-7">
              <Flex direction="column" gap={4}>
                <Box>
                  <FormLabel>Título</FormLabel>
                  <InputGroup>
                    <Input
                      value={urlData.title}
                      size="sm"
                      variant="outline"
                      onChange={(e) => {
                        setUrlData({ ...urlData, title: e.target.value })
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          save()
                        }
                      }}
                    />
                    <Button
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                      colorScheme="blue"
                      size="sm"
                      onClick={save}
                    >
                      <i className="lni lni-save" />
                    </Button>
                  </InputGroup>
                </Box>
                <Box>
                  <FormLabel>URL Original</FormLabel>
                  <Text wordBreak="break-all">
                    <Link href={urlData.original_url} isExternal color="blue.500">
                      {urlData.original_url}
                    </Link>
                  </Text>
                </Box>
                <Box>
                  <FormLabel>Total de acessos</FormLabel>
                  <Text>{urlData.access_count}</Text>
                </Box>
              </Flex>
            </div>
            <div className="col-12 col-sm-5">
              <Flex direction="column" alignItems="center" gap={4}>
                <QRCodeCanvas value={fullUrl} size={220} id="abra-qrcode-canvas" includeMargin={true} />
                <Button colorScheme="magenta" mr={3} onClick={handleDownload}>
                  Baixar PNG
                </Button>
              </Flex>
            </div>
          </div>
          <div className="row mb-4 border-top pt-4 mt-4">
            <div className="col-sm-0 col-md-2 col-lg-2 col-xl-3"></div>
            <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-6">
              <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
                Filtrar cliques
              </FormLabel>
              <InputGroup display="flex" alignItems="center" size="sm">
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-start"
                  value={filters.created_at.from}
                  onChange={(e) =>
                    setFilters({ ...filters, created_at: { ...filters.created_at, from: e.target.value } })
                  }
                  max={filters.created_at.to}
                  min="2025-07-07"
                />
                <InputAddon>Até</InputAddon>
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-end"
                  value={filters.created_at.to}
                  onChange={(e) =>
                    setFilters({ ...filters, created_at: { ...filters.created_at, to: e.target.value } })
                  }
                  min={filters.created_at.from}
                />
              </InputGroup>
            </div>
          </div>
          <Text mt={4} fontWeight="bold">
            Cliques:
          </Text>
          <Table variant="striped" mt={2} fontSize="smaller" padding="0">
            <Thead>
              <tr>
                <th>IP</th>
                <th>Dispositivo</th>
                <th>Navegador</th>
                <th>Sistema Operacional</th>
                <th>País</th>
                <th>Hora do Clique</th>
              </tr>
            </Thead>
            <Tbody>
              {filteredClicks.map((click) => (
                <tr key={click.id}>
                  <td>{click.ip_address}</td>
                  <td>{click.device_type}</td>
                  <td>{click.browser}</td>
                  <td>{click.os}</td>
                  <td>{click.country}</td>
                  <td>{new Date(click.clicked_at).toLocaleString()}</td>
                </tr>
              ))}
              {filteredClicks.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center' }}>
                    Nenhum clique encontrado para os filtros aplicados.
                  </td>
                </tr>
              )}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  ) : (
    <></>
  )
}
